import { html, nothing } from "lit";
import { FLEET_GUIDE_HINTS, HELP_HINTS } from "../control-hints.ts";
import { FLEET_GUIDE_EXAMPLE } from "../help/fleet-guide-example.ts";
import { fleetTryIdForTab, getFleetGuideTry } from "../help/fleet-guide-tries.ts";
import { renderHelpArticleScreenshots } from "../help/help-article-screenshots.ts";
import { getHelpArticle, listHelpTopicsGrouped, type HelpTopicId } from "../help/help-content.ts";
import { icons } from "../icons.ts";
import { iconForTab, titleForTab, type Tab } from "../navigation.ts";
import { renderFleetSystemConceptsPanel } from "./fleet-system-concepts-panel.ts";

export type FleetGuideModalTab = "components" | "example" | "architecture";

export type FleetGuideModalProps = {
  open: boolean;
  tab: FleetGuideModalTab;
  topic: HelpTopicId;
  onClose: () => void;
  onTabChange: (tab: FleetGuideModalTab) => void;
  onSelectTopic: (topic: HelpTopicId) => void;
  onOpenTab: (tab: Tab) => void;
  onTry: (tryId: string) => void;
};

export function renderFleetGuideModal(props: FleetGuideModalProps) {
  if (!props.open) {
    return nothing;
  }

  return html`
    <div
      class="fleet-guide-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fleet-guide-title"
      @click=${(e: Event) => {
        if (e.target === e.currentTarget) {
          props.onClose();
        }
      }}
      @keydown=${(e: KeyboardEvent) => {
        if (e.key === "Escape") {
          props.onClose();
        }
      }}
    >
      <div class="fleet-guide-card" @click=${(e: Event) => e.stopPropagation()}>
        <header class="fleet-guide-header">
          <div>
            <h2 id="fleet-guide-title" class="fleet-guide-title">Jaisiu guide</h2>
            <p class="fleet-guide-sub">What each area does, plus a multi-agent example</p>
          </div>
          <button
            type="button"
            class="btn icon fleet-guide-close"
            title=${FLEET_GUIDE_HINTS.close}
            aria-label="Close guide"
            @click=${props.onClose}
          >
            ${icons.x}
          </button>
        </header>

        <nav class="fleet-guide-tabs" aria-label="Guide sections">
          <button
            type="button"
            class="fleet-guide-tabs__btn ${props.tab === "components" ? "fleet-guide-tabs__btn--active" : ""}"
            title=${FLEET_GUIDE_HINTS.tabComponents}
            aria-label=${FLEET_GUIDE_HINTS.tabComponents}
            aria-selected=${props.tab === "components"}
            @click=${() => props.onTabChange("components")}
          >
            Components
          </button>
          <button
            type="button"
            class="fleet-guide-tabs__btn ${props.tab === "architecture" ? "fleet-guide-tabs__btn--active" : ""}"
            aria-selected=${props.tab === "architecture"}
            @click=${() => props.onTabChange("architecture")}
          >
            Architecture
          </button>
          <button
            type="button"
            class="fleet-guide-tabs__btn ${props.tab === "example" ? "fleet-guide-tabs__btn--active" : ""}"
            title=${FLEET_GUIDE_HINTS.tabExample}
            aria-label=${FLEET_GUIDE_HINTS.tabExample}
            aria-selected=${props.tab === "example"}
            @click=${() => props.onTabChange("example")}
          >
            Example
          </button>
        </nav>

        <div class="fleet-guide-body">
          ${
            props.tab === "components"
              ? renderComponentsPanel(props)
              : props.tab === "architecture"
                ? renderArchitecturePanel()
                : renderExamplePanel(props)
          }
        </div>
      </div>
    </div>
  `;
}

function renderArchitecturePanel() {
  return html`
    <section class="fleet-guide-architecture">${renderFleetSystemConceptsPanel()}</section>
  `;
}

function renderComponentsPanel(props: FleetGuideModalProps) {
  const article = getHelpArticle(props.topic);
  return html`
    <section class="fleet-guide-components help-layout">
      <aside class="help-sidebar" aria-label="Fleet components">
        ${listHelpTopicsGrouped().map(
          (group) => html`
            <div class="help-sidebar__group">
              <div class="help-sidebar__label">${group.label}</div>
              <ul class="help-sidebar__list">
                ${group.topics.map((topicId) => {
                  const item = getHelpArticle(topicId);
                  const iconKey = topicId === "intro" ? "book" : iconForTab(topicId as Tab);
                  return html`
                    <li>
                      <button
                        type="button"
                        class="help-sidebar__item ${props.topic === topicId ? "help-sidebar__item--active" : ""}"
                        @click=${() => props.onSelectTopic(topicId)}
                      >
                        <span class="help-sidebar__icon" aria-hidden="true">${icons[iconKey]}</span>
                        <span class="help-sidebar__text">
                          <span class="help-sidebar__title">${item.title}</span>
                        </span>
                      </button>
                    </li>
                  `;
                })}
              </ul>
            </div>
          `,
        )}
      </aside>
      <article class="help-article">
        <header class="help-article__header">
          <h2 class="help-article__title">${article.title}</h2>
          <p class="help-article__tagline">${article.tagline}</p>
        </header>
        <div class="help-article__body">
          <section class="help-block">
            <h3 class="help-block__title">What it is</h3>
            <p class="help-block__text">${article.overview}</p>
          </section>
          ${renderHelpArticleScreenshots(article.images)}
          <section class="help-block">
            <h3 class="help-block__title">How to use it</h3>
            <ol class="help-steps">
              ${article.steps.map((step) => html`<li>${step}</li>`)}
            </ol>
          </section>
        </div>
      </article>
    </section>
  `;
}

function renderExamplePanel(props: FleetGuideModalProps) {
  const ex = FLEET_GUIDE_EXAMPLE;
  return html`
    <article class="fleet-guide-example">
      <header class="fleet-guide-example__header">
        <h3 class="fleet-guide-example__title">${ex.title}</h3>
        <p class="fleet-guide-example__intro">${ex.intro}</p>
      </header>
      <ol class="fleet-guide-example__phases">
        ${ex.phases.map(
          (phase) => html`
            <li class="fleet-guide-example__phase">
              <h4 class="fleet-guide-example__phase-title">${phase.title}</h4>
              <p class="fleet-guide-example__phase-summary">${phase.summary}</p>
              <ol class="help-steps">
                ${phase.steps.map((step) => html`<li>${step}</li>`)}
              </ol>
              ${
                phase.relatedTab
                  ? renderPhaseActions(phase.relatedTab, phase.tryId, props)
                  : nothing
              }
            </li>
          `,
        )}
      </ol>
      <section class="help-block help-block--tips fleet-guide-example__outcome">
        <h3 class="help-block__title">Outcome</h3>
        <p class="help-block__text">${ex.outcome}</p>
      </section>
    </article>
  `;
}

function renderPhaseActions(tab: Tab, tryId: string | undefined, props: FleetGuideModalProps) {
  const resolvedTryId = tryId ?? fleetTryIdForTab(tab);
  const canTry = Boolean(getFleetGuideTry(resolvedTryId));
  return html`
    <div class="fleet-guide-example__actions">
      <button
        type="button"
        class="btn fleet-guide-example__go"
        title=${FLEET_GUIDE_HINTS.openTab(titleForTab(tab))}
        aria-label=${FLEET_GUIDE_HINTS.openTab(titleForTab(tab))}
        @click=${() => {
          props.onOpenTab(tab);
          props.onClose();
        }}
      >
        <span class="fleet-guide-example__go-icon" aria-hidden="true"
          >${icons[iconForTab(tab)]}</span
        >
        Open ${titleForTab(tab)}
      </button>
      ${
        canTry
          ? html`
              <button
                type="button"
                class="btn primary fleet-guide-example__go"
                title=${HELP_HINTS.tryExample(resolvedTryId)}
                aria-label=${HELP_HINTS.tryExample(resolvedTryId)}
                @click=${() => {
                  props.onTry(resolvedTryId);
                  props.onClose();
                }}
              >
                Try
                <span class="fleet-guide-example__try-id mono">${resolvedTryId}</span>
              </button>
            `
          : nothing
      }
    </div>
  `;
}
